import React, { useEffect, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Flame, Wrench, Thermometer, ShieldCheck, Phone, Droplets, CheckCircle2, ArrowRight, ArrowLeft, Calendar, X } from 'lucide-react';
import { getServiceBySlug, services } from '../data/services';
const iconMap = {
  Flame,
  Wrench,
  Thermometer,
  ShieldCheck,
  Phone,
  Droplets
};
export function ServiceDetail() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const service = slug ? getServiceBySlug(slug) : undefined;
  const [lightbox, setLightbox] = useState<string | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  if (!service) {
    return <Navigate to="/" replace data-id="element-287" data-mp="13r5vk7i75rkp" />;
  }
  const Icon = iconMap[service.iconName];
  const otherServices = services.filter(s => s.slug !== service.slug).slice(0, 3);
  return <div className="pt-28 pb-20 bg-[#0a0a0a]" data-id="element-288" data-mp="3ogv8gezcb3ar">
      {/* Breadcrumb */}
      <div className="max-w-7xl mx-auto px-6 mb-8" data-id="element-289" data-mp="18ghwkt0g4v17">
        <Link to="/" className="inline-flex items-center gap-2 text-gray-400 hover:text-[#FFD600] transition-colors text-sm" data-id="element-290" data-mp="18hsxml2iboom">
          <ArrowLeft className="w-4 h-4" data-id="element-291" data-mp="zh37nxzxgsit" />
          Back to Home
        </Link>
      </div>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6" data-id="element-292" data-mp="13gvkimwctktz">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center" data-id="element-293" data-mp="13jrigb87c9if">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5
        }} data-id="element-294" data-mp="pp78ejm3uwd0">
            <div className="inline-flex items-center gap-3 mb-6" data-id="element-295" data-mp="ks70s49lxljs">
              <div className="w-12 h-12 rounded-full bg-gradient-primary p-[1px]" data-id="element-296" data-mp="6r4crlz1y168">
                <div className="w-full h-full bg-[#0a0a0a] rounded-full flex items-center justify-center" data-id="element-297" data-mp="bqr0ipciazta">
                  <Icon className="w-5 h-5 text-[#FF9500]" data-id="element-298" data-mp="2dfmvkzg6cea4" />
                </div>
              </div>
              <span className="text-[#FFD600] font-bold tracking-widest uppercase text-xs" data-id="element-299" data-mp="28pg7r1thkxsx">
                Our Service
              </span>
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-6" data-id="element-300" data-mp="23qd0rbty4hib">
              {service.title}
            </h1>
            <p className="text-lg text-gray-400 mb-8 leading-relaxed" data-id="element-301" data-mp="2wfajfg22kgbl">
              {service.longDescription}
            </p>
            <div className="flex flex-col sm:flex-row gap-4" data-id="element-302" data-mp="2rgz8t33ya1sb">
              <a href="tel:07708233554" className="inline-flex items-center justify-center gap-2 bg-gradient-primary text-black font-bold px-6 py-3 rounded-full hover:shadow-[0_0_30px_rgba(255,149,0,0.3)] transition-all" data-id="element-303" data-mp="2ndubrg2n2sb2">
                <Phone className="w-5 h-5" data-id="element-304" data-mp="1bwukmns12jim" />
                Call 07708 233554
              </a>
              <Link to="/#contact" className="inline-flex items-center justify-center gap-2 border-2 border-[#FFD600] text-[#FFD600] font-bold px-6 py-3 rounded-full hover:bg-[#FFD600]/10 transition-all" data-id="element-305" data-mp="171xcdj2koe6s">
                <Calendar className="w-5 h-5" data-id="element-306" data-mp="3rmtaq04o4imi" />
                Get a Free Quote
              </Link>
            </div>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="relative" data-id="element-307" data-mp="bb3do7rn2jf">
            <div className="absolute inset-0 bg-gradient-primary rounded-3xl blur-2xl opacity-20 transform rotate-3" data-id="element-308" data-mp="j7sr7ujhuiae"></div>
            <div className="relative rounded-3xl overflow-hidden border border-white/10 bg-brand-card aspect-[4/5] md:aspect-[5/6]" data-id="element-309" data-mp="2gyvqj81l01s4">
              <img src={service.heroImage} alt={service.title} className="w-full h-full object-cover" data-id="element-310" data-mp="2kzpd9y3afajf" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" data-id="element-311" data-mp="1lmm6bklfqm4m"></div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 mt-24" data-id="element-312" data-mp="1upnc1k6e2b7r">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-10" data-id="element-313" data-mp="16yres7j5ynt4">
          <div className="lg:col-span-1" data-id="element-314" data-mp="16z12tpknekw6">
            <span className="text-[#FFD600] font-bold tracking-widest uppercase text-xs mb-3 block" data-id="element-315" data-mp="1206n4whz9cga">
              What's Included
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-white leading-tight" data-id="element-316" data-mp="1gpvr0zhgfuk9">
              Done right, the first time.
            </h2>
          </div>
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-317" data-mp="o7i2jqi0dkxu">
            {service.features.map((feature, i) => <motion.div key={i} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: i * 0.05
          }} className="flex items-start gap-3 bg-brand-card border border-white/5 rounded-xl p-5" data-id="element-318" data-mp="j8ni6k4e7obm" data-mpi={i}>
                <CheckCircle2 className="w-5 h-5 text-[#FFD600] flex-shrink-0 mt-0.5" data-id="element-319" data-mp="3im4koz5adbmx" />
                <span className="text-white font-medium" data-id="element-320" data-mp="3ijqub3ljwnlp">{feature}</span>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Gallery */}
      <section className="max-w-7xl mx-auto px-6 mt-24" data-id="element-321" data-mp="39jlm4r8j9l6h">
        <div className="mb-10" data-id="element-322" data-mp="1t6x2ugo8exrr">
          <span className="text-[#FFD600] font-bold tracking-widest uppercase text-xs mb-3 block" data-id="element-323" data-mp="1t5lq3rct6foi">
            Recent Work
          </span>
          <h2 className="text-3xl md:text-4xl font-black text-white" data-id="element-324" data-mp="3q1h4i00ibyg">
            From Our {service.title} Jobs
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-325" data-mp="3uxdt8im1fz7i">
          {service.gallery.map((src, i) => <motion.button key={i} type="button" onClick={() => setLightbox(src)} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: i * 0.1
        }} className="group relative aspect-square rounded-2xl overflow-hidden border border-white/5 bg-brand-card" aria-label={`Open ${service.title} photo ${i + 1}`} data-id="element-326" data-mp="3q42s8y3wqcni" data-mpi={i}>
              <img src={src} alt={`${service.title} job ${i + 1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-327" data-mp="11da0o7ggdaxt" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-328" data-mp="2o985wtkquli2"></div>
            </motion.button>)}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 mt-24" data-id="element-329" data-mp="2x4nuow6n6ags">
        <div className="relative overflow-hidden rounded-3xl border border-white/10 bg-brand-card p-10 md:p-16 text-center" data-id="element-330" data-mp="20dluusp9b7hr">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-gradient-radial from-[#FF9500]/10 to-transparent rounded-full blur-3xl pointer-events-none" data-id="element-331" data-mp="25bx6n8z43s2m"></div>
          <div className="relative" data-id="element-332" data-mp="2ea8w37o8sjth">
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4" data-id="element-333" data-mp="3ksxb1il3660j">
              Ready to get started?
            </h2>
            <p className="text-gray-400 max-w-xl mx-auto mb-8" data-id="element-334" data-mp="3pq77vjat1jye">
              Get a free, no-obligation quote for your{' '}
              {service.title.toLowerCase()} from a local Gas Safe registered
              engineer.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-335" data-mp="2we0soju9fqlb">
              <a href="tel:07708233554" className="inline-flex items-center justify-center gap-2 bg-gradient-primary text-black font-bold px-8 py-4 rounded-full hover:shadow-[0_0_30px_rgba(255,149,0,0.3)] transition-all" data-id="element-336" data-mp="3a9cutcaqwjc9">
                <Phone className="w-5 h-5" data-id="element-337" data-mp="3f5jwgsw3z8oe" />
                Call Now
              </a>
              <Link to="/#contact" className="inline-flex items-center justify-center gap-2 border-2 border-[#FFD600] text-[#FFD600] font-bold px-8 py-4 rounded-full hover:bg-[#FFD600]/10 transition-all" data-id="element-338" data-mp="3t531vdxjc5x0">
                Get a Free Quote
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Other Services */}
      <section className="max-w-7xl mx-auto px-6 mt-24" data-id="element-339" data-mp="1knmcrzk09cua">
        <div className="mb-10 flex items-end justify-between flex-wrap gap-4" data-id="element-340" data-mp="1oo54zupt8kx0">
          <div data-id="element-341" data-mp="1oq9664wbr5tl">
            <span className="text-[#FFD600] font-bold tracking-widest uppercase text-xs mb-3 block" data-id="element-342" data-mp="1awroyqr4256q">
              Explore More
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-white" data-id="element-343" data-mp="1fujreepdt9p6">
              Other Services
            </h2>
          </div>
          <Link to="/#services" className="inline-flex items-center gap-2 text-[#FFD600] font-semibold hover:text-white transition-colors" data-id="element-344" data-mp="110op5rjqeimy">
            View all <ArrowRight className="w-4 h-4" data-id="element-345" data-mp="rzqh8y669qz3" />
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-346" data-mp="wy0cf8rwcrkp">
          {otherServices.map(s => {
          const OtherIcon = iconMap[s.iconName];
          return <Link key={s.slug} to={`/services/${s.slug}`} className="group relative bg-brand-card rounded-2xl overflow-hidden border border-white/5 hover:border-white/10 transition-all duration-300 hover:-translate-y-1" data-id="element-347" data-mp="2rbjg70cit6eg">
                <div className="aspect-video overflow-hidden" data-id="element-348" data-mp="i109ixlwimew">
                  <img src={s.heroImage} alt={s.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-id="element-349" data-mp="i2l2iiskhxan" />
                </div>
                <div className="p-6" data-id="element-350" data-mp="1er99mk2qw05a">
                  <div className="w-12 h-12 rounded-full bg-gradient-primary p-[1px] mb-4" data-id="element-351" data-mp="1eq7xsddwn9mi">
                    <div className="w-full h-full bg-brand-card rounded-full flex items-center justify-center" data-id="element-352" data-mp="10qq2df63sy46">
                      <OtherIcon className="w-5 h-5 text-[#FF9500]" data-id="element-353" data-mp="15ohxsj5yj2d4" />
                    </div>
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2" data-id="element-354" data-mp="3ljtjz1jduv4u">
                    {s.title}
                  </h3>
                  <p className="text-gray-400 text-sm mb-4 leading-relaxed" data-id="element-355" data-mp="3hz0660l0xd4t">
                    {s.shortDescription}
                  </p>
                  <span className="inline-flex items-center gap-1 text-[#FFD600] font-semibold text-sm" data-id="element-356" data-mp="3dxelvu65twkl">
                    Learn more <ArrowRight className="w-4 h-4" data-id="element-357" data-mp="3rwxqiczrips5" />
                  </span>
                </div>
              </Link>;
        })}
        </div>
      </section>

      {/* Lightbox */}
      {lightbox && <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-6" onClick={() => setLightbox(null)} role="dialog" aria-modal="true" data-id="element-358" data-mp="2z6paorsp8hgq">
          <button onClick={() => setLightbox(null)} className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20" aria-label="Close" data-id="element-359" data-mp="348elq7fx8v4t">
            <X className="w-5 h-5" data-id="element-360" data-mp="2bixuve7y9map" />
          </button>
          <img src={lightbox} alt="Enlarged view" className="max-h-[90vh] max-w-[90vw] object-contain rounded-2xl" onClick={e => e.stopPropagation()} data-id="element-361" data-mp="fgui0d12ean5" />
        </div>}
    </div>;
}